import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { AnswerResult } from "@/lib/types";

interface Props {
  result: AnswerResult | null;
  onNext: () => void;
}

/**
 * Slides up from the bottom after an answer. Tap anywhere (or wait briefly on
 * correct) to advance. On wrong, the user must tap so they have time to read
 * the explanation. Once dismissed, the overlay becomes pointer-events-none so
 * its exit animation doesn't eat taps meant for the next question's buttons.
 */
export function FeedbackOverlay({ result, onNext }: Props) {
  const fired = useRef(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fired.current = false;
    setDismissed(false);
    if (!result) return;
    if (!result.correct) return;
    const t = setTimeout(() => {
      if (!fired.current) {
        fired.current = true;
        setDismissed(true);
        onNext();
      }
    }, 1400);
    return () => clearTimeout(t);
  }, [result, onNext]);

  function dismiss() {
    if (fired.current) return;
    fired.current = true;
    setDismissed(true);
    onNext();
  }

  return (
    <AnimatePresence>
      {result && (
        <motion.button
          type="button"
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", stiffness: 500, damping: 40 }}
          onClick={dismiss}
          style={{ pointerEvents: dismissed ? "none" : "auto" }}
          className={`fixed inset-x-0 bottom-0 z-40 text-left block w-full ${
            result.correct
              ? "bg-brand-mint/95 text-brand-ink"
              : "bg-brand-rose/95 text-white"
          }`}
        >
          <div className="safe-pb rounded-t-chunk px-5 pt-5 shadow-pop border-t border-current/20">
            <div className="flex items-center gap-3">
              <span className="text-3xl">
                {result.correct ? "✅" : result.user_answer.trim() ? "😵" : "📝"}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl leading-tight">
                  {result.correct
                    ? "¡Muy bien!"
                    : result.user_answer.trim()
                      ? "Casi…"
                      : "Anota esta"}
                </div>
                <div className="text-sm font-bold opacity-90 break-words">
                  Correcto:{" "}
                  <span className="font-black">{result.correct_answer}</span>
                  {result.ai_adjudicated && (
                    <span className="ml-2 pill bg-white/50 border-white/30">IA</span>
                  )}
                </div>
              </div>
            </div>
            {result.explanation && (
              <p className="mt-3 text-sm font-bold leading-snug bg-white/30 rounded-2xl px-3 py-2">
                {result.explanation}
              </p>
            )}
            <div
              className={`mt-4 w-full chunk-btn ${
                result.correct
                  ? "bg-brand-ink text-white"
                  : "bg-white text-brand-rose"
              }`}
            >
              {result.correct ? "Continuar →" : "Entendido · siguiente →"}
            </div>
          </div>
        </motion.button>
      )}
    </AnimatePresence>
  );
}
